import React, {useContext, useState} from 'react';
import Grid from "@mui/material/Grid2";
import {
    Alert,
    Box,
    Button, CircularProgress, Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Paper, TextField,
    Typography
} from "@mui/material";
import CalculateTwoToneIcon from "@mui/icons-material/CalculateTwoTone";
import AddBoxTwoToneIcon from '@mui/icons-material/AddBoxTwoTone';
import {useNavigate} from "react-router";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {calculations, loadExcelSTDMonthly} from "../../apis/calculationApi";
import PrivacyTipTwoToneIcon from "@mui/icons-material/PrivacyTipTwoTone";
import {DataGrid} from "@mui/x-data-grid";
import Notification from "../../components/Notification";
import {LanguageContext} from "../../components/LanguageContext";
import {useDataGridLocaleText} from "../../utils/dataGridLocale";
import {styled} from "@mui/material/styles";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const MonthlyStdCalculationsPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {translations} = useContext(LanguageContext);
    const dgLocale = useDataGridLocaleText();

    const [number, setNumber] = useState(5);
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const [type, setType] = useState(null);
    const [openN, setOpenN] = useState(false);
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState(null);

    const [open, setOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    }

    const {data, isLoading, isError} = useQuery({
       queryKey: ['calculations', 0, 0],
       queryFn: () => calculations(0, 0),
    });

    const mutation = useMutation({
        mutationFn: (formData) => loadExcelSTDMonthly(formData),
        onSuccess: (response) => {
            setType(1);
            setMessage(response.data.message);
            setOpenN(true);
            setFile(null);
            setLoading(false);

            queryClient.invalidateQueries(['calculations', 0, 0]).then((result) => {});

            setTimeout(() => {
                navigate('../../../calculation/' + response.data.id);
            }, 2000);
        },
        onError: (error) => {
            setType(2);
            setMessage(error.response?.data?.message || translations.uploadGenericError);
            setErrors(error.response?.data?.errors || null);
            setOpenN(true);
            setLoading(false);
        }
    });

    const columns = [
        {
            field: "id",
            headerName: translations.calcId,
            minWidth: 50,
            headerAlign: "center",
            align: "center",
        },

        {
            field: "korisnik",
            headerName: translations.calcUser,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "naziv",
            headerName: translations.calcFileName,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "datum",
            headerName: translations.calcDateCreated,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                const date = new Date(params.row.datum);
                return (
                    <span>{date.getDate()}/{(date.getMonth() + 1)}/{date.getFullYear()} | {date.getHours()}:{date.getMinutes()}:{date.getSeconds()}</span>
                );
            }
        },
        {
            field: "informacije",
            headerName: translations.calcInformation,
            minWidth: 200,
            flex: 1,
            headerAlign: "center",
            align: "center",
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        sx={{width: "100%", alignSelf: "center"}}
                        endIcon={<PrivacyTipTwoToneIcon/>}
                        onClick={() => navigate("../calculation/" + params.row.id)}
                    >
                        {translations.calcInformationButton}
                    </Button>
                );
            },
        },
    ];

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const allowedExtensions = ['xlsx', 'xls'];
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (allowedExtensions.includes(fileExtension)) {
                setFile(file);
                setType(1);
                setMessage(translations.uploadFileReady);
                setOpenN(true);
            } else {
                setType(2);
                setMessage(translations.uploadInvalidFileType);
                setOpenN(true);
                event.target.value = null;
            }
        }
    };

    const handleUploadExcel = (event) => {
        event.preventDefault();
        setLoading(true);

        if (number < 5) {
            setType(2);
            setMessage(translations.uploadMinNumber);
            setOpenN(true);
            setLoading(false);
            return;
        }

        if (file == null) {
            setType(2);
            setMessage(translations.uploadFileRequired);
            setOpenN(true);
            setLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('id_korisnik', localStorage.getItem('user_id'));
        formData.append('file', file);
        formData.append('mesec_nedelja', 0);
        formData.append('broj_meseci_nedelja', number);
        formData.append('std_fp', 0);

        mutation.mutateAsync(formData).catch((err) => {});
    }

    return (
        <Grid container spacing={2}>
            <Notification
                type={type}
                message={message}
                errors={errors}
                open={openN}
                setOpen={setOpenN}
            />
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    <Typography sx={{fontSize: 22, textAlign: "center"}}>
                        <CalculateTwoToneIcon size={22} sx={{mr: 2}}/>
                        {translations.stdMonthlyTitle}
                    </Typography>
                </Paper>
            </Grid>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        endIcon={loading ? null : <AddBoxTwoToneIcon/>}
                        onClick={handleClickOpen}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={25} color="inherit"/> : translations.stdNewMonthly}
                    </Button>
                </Paper>
            </Grid>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center", height: 600}}>
                    {isError ? (
                        <Alert severity="error">
                            {translations.stdErrorLoad}
                        </Alert>
                    ) : isLoading ? (
                        <LinearProgress size={24} color="inherit"/>
                    ) : (
                        <DataGrid
                            columns={columns}
                            rows={isLoading ? [] : data?.data}
                            localeText={dgLocale}
                        />
                    )}
                </Paper>
            </Grid>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {translations.uploadDialogTitle}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" noValidate onSubmit={handleUploadExcel}>
                        <TextField
                            fullWidth
                            margin="dense"
                            id="broj_meseci_nedelja"
                            label={translations.uploadNumberLabel}
                            placeholder={translations.uploadNumberPlaceholder}
                            variant="outlined"
                            required
                            value={number}
                            onChange={(event) => setNumber(event.target.value)}
                            helperText={translations.uploadNumberHelper}
                        />
                        <Button
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                        >
                            {translations.uploadButton} |{" "}{file == null ? ` ${translations.uploadNoFileSelected}` : ` (${file.name})`}
                            <VisuallyHiddenInput
                                type="file"
                                onChange={handleFileChange}
                                accept=".xlsx, .xls"
                            />
                        </Button>

                        <DialogActions>
                            <Button onClick={handleClose} color="error">{translations.cancel}</Button>
                            <Button type="submit" variant="contained" color="success" onClick={handleClose}>
                                {translations.confirm}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default MonthlyStdCalculationsPage;